import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container, Typography, Box, Paper, Divider, CircularProgress, Alert,
  Chip, Button, Avatar, TextField, Tooltip, IconButton, Menu, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox, Snackbar
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  ArrowBack, ArrowUpward, Favorite, EmojiObjects, SentimentVerySatisfied,
  Reply as ReplyIcon, MoreVert, Lock, PushPin, Flag, Verified, Edit as EditIcon,
  SwapHoriz, Visibility, VisibilityOff
} from '@mui/icons-material';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { formatDistanceToNow, parseISO } from 'date-fns';
import hu from 'date-fns/locale/hu';
import DOMPurify from 'dompurify';
import forumService from '../../services/forumService';
import DialogHeader from '../../components/Common/DialogHeader';
import QuillEditor from '../../components/Common/QuillEditor';
import { useAuth } from '../../contexts/AuthContext';

const QUILL_MODULES = {
  toolbar: {
    container: [
      [{ header: [2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      ['clean']
    ],
    handlers: {
      link: function(value) {
        if (value) {
          // eslint-disable-next-line no-alert
          const url = prompt('Link URL:');
          if (!url) return;
          const quill = this.quill;
          const range = quill.getSelection();
          if (!range) return;
          const selectedText = range.length > 0 ? quill.getText(range.index, range.length) : '';
          if (!selectedText.trim()) {
            quill.deleteText(range.index, range.length);
            quill.insertText(range.index, 'link', 'link', url);
            quill.setSelection(range.index + 4);
          } else {
            quill.format('link', url);
          }
        } else {
          this.quill.format('link', false);
        }
      }
    }
  }
};

const reactionConfig = [
  { type: 'UPVOTE', icon: <ArrowUpward fontSize="small" />, label: 'Upvote' },
  { type: 'HEART', icon: <Favorite fontSize="small" />, label: 'Szívecske' },
  { type: 'HELPFUL', icon: <EmojiObjects fontSize="small" />, label: 'Hasznos' },
  { type: 'FUNNY', icon: <SentimentVerySatisfied fontSize="small" />, label: 'Vicces' },
];

const formatDate = (dateStr) => {
  try { return formatDistanceToNow(parseISO(dateStr), { addSuffix: true, locale: hu }); }
  catch { return ''; }
};

const stripHtml = (html) => html.replace(/<[^>]*>/g, '').trim();

const buildTree = (flat) => {
  const map = {};
  flat.forEach(r => { map[r.id] = { ...r, children: [] }; });
  const roots = [];
  flat.forEach(r => {
    if (r.parentId && map[r.parentId]) {
      map[r.parentId].children.push(map[r.id]);
    } else {
      roots.push(map[r.id]);
    }
  });
  return roots;
};

const ReplyFormContext = createContext(null);

const AuthorLabel = ({ author, isAnonymous, isExpert }) => {
  const navigate = useNavigate();
  if (isAnonymous) return (
    <Typography variant="body2" color="text.secondary" fontStyle="italic">Névtelen felhasználó</Typography>
  );
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 0.75, rowGap: 0.25, flexWrap: 'wrap', minWidth: 0 }}>
      <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
        {author?.firstName} {author?.lastName}
      </Typography>
      {author?.userType === 'PROVIDER' && author?.provider && (
        <Tooltip title={`Ugrás a profiljára: ${author.provider.businessName}`}>
          <Chip
            icon={<Verified sx={{ fontSize: 12 }} />}
            label={author.provider.businessName}
            size="small"
            color="primary"
            onClick={() => navigate(`/szolgaltato/${author.provider.slug}`)}
            sx={{ height: 22, fontSize: 11, cursor: 'pointer', maxWidth: '100%' }}
          />
        </Tooltip>
      )}
      {isExpert && (
        <Chip label="✓ Szakértő" size="small" color="success" sx={{ height: 22, fontSize: 11 }} />
      )}
    </Box>
  );
};

const ReactionBar = ({ reactions, userReactions, onReact }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {reactionConfig.map(({ type, icon, label }) => {
        const count = reactions?.[type] ?? 0;
        const active = userReactions?.includes(type);
        return (
          <Tooltip key={type} title={label}>
            <Button
              size="small"
              aria-label={label}
              aria-pressed={!!active}
              onClick={() => onReact(type)}
              sx={{
                minWidth: 36,
                minHeight: { xs: 36, sm: 32 },
                px: count > 0 ? 1.25 : 1,
                py: 0.25,
                fontSize: 13,
                lineHeight: 1,
                color: active ? theme.palette.primary.main : 'text.secondary',
                bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                border: `1px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
                borderRadius: 5,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: count > 0 ? 0.5 : 0,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                '& .MuiButton-startIcon': { display: 'none' }
              }}
            >
              {icon}
              {count > 0 && <span>{count}</span>}
            </Button>
          </Tooltip>
        );
      })}
    </Box>
  );
};

// Bejelentés párbeszédablak: azonnali validáció (5-500 karakter), hibakezelés és visszajelzés
const ReportDialog = ({ open, onClose, title, onSubmit }) => {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  const trimmed = reason.trim();
  const fieldError = trimmed.length < 5
    ? (trimmed.length === 0 ? 'Add meg a bejelentés okát.' : `Legalább 5 karakter szükséges (még ${5 - trimmed.length}).`)
    : null;

  const handleClose = () => {
    if (submitting) return;
    onClose();
    setReason(''); setTouched(false); setError(null);
  };

  const handleSend = async () => {
    setTouched(true);
    if (fieldError) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(trimmed);
      setSent(true);
      onClose();
      setReason(''); setTouched(false);
    } catch (err) {
      setError(err?.message || 'A bejelentés küldése nem sikerült. Próbáld újra.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader icon={<Flag />} title={title} onClose={handleClose} />
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
          <TextField
            autoFocus
            required
            label="Mi a probléma?"
            multiline
            minRows={3}
            fullWidth
            value={reason}
            onChange={e => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            error={touched && !!fieldError}
            helperText={
              <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <span>{touched && fieldError ? fieldError : 'Röviden írd le, miért kifogásolod a tartalmat.'}</span>
                <span>{reason.length}/500</span>
              </Box>
            }
            inputProps={{ maxLength: 500 }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={handleClose} disabled={submitting}>Mégse</Button>
          <Button onClick={handleSend} variant="contained" disabled={submitting}>
            {submitting ? 'Küldés...' : 'Bejelentés'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={sent}
        autoHideDuration={4000}
        onClose={() => setSent(false)}
        message="Köszönjük, a bejelentést megkaptuk."
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

const ReplyCard = ({ reply, onReact, onReply, isAdmin, onHide, onExpert, onEdit, currentUserId, isAuthenticated, depth = 0 }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const replyFormCtx = useContext(ReplyFormContext);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState(reply.content);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  const isOwner = reply.isOwnPost;
  const hasMenuItems = isOwner || isAdmin;
  const actionBtnSx = { p: { xs: 1, sm: 0.75 } };

  const handleReport = async (reason) => {
    const res = await forumService.report({ replyId: reply.id, reason });
    if (res && res.success === false) throw new Error(res.message);
  };

  const handleEditSave = async () => {
    if (stripHtml(editContent).length < 2) {
      setEditError('A válasz legalább 2 karakter legyen.');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await onEdit(reply.id, editContent);
      setEditMode(false);
    } catch (err) {
      setEditError(err?.message || 'A mentés nem sikerült.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleContentClick = (e) => {
    if (e.target.tagName === 'A') {
      if (!isAuthenticated) {
        e.preventDefault();
        navigate('/login');
      }
    }
  };

  return (
    <Box
      id={`reply-${reply.id}`}
      sx={{
        ml: depth === 1 || depth === 2 ? { xs: 2, sm: 4 } : 0,
        pl: depth === 1 || depth === 2 ? { xs: 1, sm: 2 } : 0,
        borderLeft: depth === 1 || depth === 2 ? `2px solid ${theme.palette.divider}` : 'none',
        mt: depth > 0 ? 2 : 0,
        mb: 2
      }}
    >
      {reply.isHidden ? (
        <Typography variant="body2" color="text.disabled" fontStyle="italic">[Válasz elrejtve]</Typography>
      ) : (
        <Box>
          {reply.isExpertAnswer && (
            <Box sx={{
              bgcolor: alpha(theme.palette.success.main, 0.08),
              border: `1px solid ${theme.palette.success.main}`,
              borderRadius: 1,
              px: 1.5,
              py: 0.5,
              mb: 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5
            }}>
              <Verified color="success" sx={{ fontSize: 14 }} />
              <Typography variant="caption" color="success.main" fontWeight={600}>Szakértői válasz</Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 } }}>
            <Avatar
              src={!reply.isAnonymous && reply.author?.avatarUrl ? reply.author.avatarUrl : undefined}
              sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, bgcolor: theme.palette.primary.main, fontSize: { xs: 12, sm: 14 }, flexShrink: 0 }}
            >
              {reply.isAnonymous ? '?' : (reply.author?.firstName?.[0] ?? '?')}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <AuthorLabel
                  author={reply.author}
                  isAnonymous={reply.isAnonymous}
                  isExpert={reply.isExpertAnswer}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{formatDate(reply.createdAt)}</Typography>
                  {reply.isEdited && (
                    <Typography variant="caption" color="text.disabled" fontStyle="italic" sx={{ display: { xs: 'none', sm: 'inline' } }}>(szerk.)</Typography>
                  )}
                </Box>
              </Box>

              {editMode ? (
                <Box sx={{ mt: 1, mb: 1 }}>
                  <Box sx={{
                    mb: 1,
                    overflow: 'hidden',
                    '& .ql-editor': { minHeight: 60, maxHeight: 200, overflowY: 'auto', wordBreak: 'break-word', overflowX: 'hidden' }
                  }}>
                    <QuillEditor
                      value={editContent}
                      onChange={setEditContent}
                      modules={QUILL_MODULES}
                    />
                  </Box>
                  {editError && <Alert severity="error" sx={{ mb: 1 }}>{editError}</Alert>}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" onClick={handleEditSave} disabled={editSaving}>
                      {editSaving ? 'Mentés...' : 'Mentés'}
                    </Button>
                    <Button size="small" disabled={editSaving} onClick={() => { setEditMode(false); setEditError(null); setEditContent(reply.content); }}>Mégse</Button>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{ mt: 0.5, mb: 1, maxWidth: '100%', '& p': { margin: 0 }, wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(reply.content) }}
                  onClick={handleContentClick}
                />
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                <ReactionBar
                  reactions={reply.reactionCounts}
                  userReactions={reply.userReactions}
                  onReact={(type) => onReact(type, null, reply.id)}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <Tooltip title="Válasz">
                    <IconButton size="small" aria-label="Válasz" sx={actionBtnSx} onClick={() => onReply(reply)}>
                      <ReplyIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Bejelentés">
                    <IconButton size="small" aria-label="Bejelentés" sx={actionBtnSx} onClick={() => setReportOpen(true)}>
                      <Flag sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  {isAdmin && (
                    <Tooltip title={reply.isHidden ? 'Visszahozás' : 'Elrejtés'}>
                      <IconButton size="small" aria-label={reply.isHidden ? 'Visszahozás' : 'Elrejtés'} sx={actionBtnSx} onClick={() => onHide(reply.id)}>
                        {reply.isHidden ? <Visibility sx={{ fontSize: 20 }} /> : <VisibilityOff sx={{ fontSize: 20 }} />}
                      </IconButton>
                    </Tooltip>
                  )}
                  {hasMenuItems && (
                    <>
                      <IconButton size="small" aria-label="További műveletek" sx={actionBtnSx} onClick={(e) => setMenuAnchor(e.currentTarget)}>
                        <MoreVert sx={{ fontSize: 20 }} />
                      </IconButton>
                      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                        {isOwner && (
                          <MenuItem onClick={() => { setEditMode(true); setMenuAnchor(null); }}>
                            <EditIcon fontSize="small" sx={{ mr: 1 }} />Szerkesztés
                          </MenuItem>
                        )}
                        {isAdmin && (
                          <MenuItem onClick={() => { onExpert(reply.id); setMenuAnchor(null); }}>
                            {reply.isExpertAnswer ? 'Expert jelölés levétele' : 'Szakértői válasz jelölése'}
                          </MenuItem>
                        )}
                      </Menu>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Inline reply form */}
          {replyFormCtx?.replyingTo?.id === reply.id && (
            <Box sx={{ mt: 2 }}>
              <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Válasz: {reply.isAnonymous ? 'Névtelen felhasználó' : reply.author?.firstName}
                </Typography>
                <Box sx={{ mb: 1, '& .ql-editor': { minHeight: 80, maxHeight: 200, overflowY: 'auto', wordBreak: 'break-word', overflowX: 'hidden' } }}>
                  <QuillEditor
                    ref={replyFormCtx.quillEditorRef}
                    value={replyFormCtx.replyContent}
                    onChange={replyFormCtx.setReplyContent}
                    modules={QUILL_MODULES}
                    placeholder="Írd ide a válaszodat..."
                  />
                </Box>
                {replyFormCtx.replyError && <Alert severity="error" sx={{ mb: 1 }}>{replyFormCtx.replyError}</Alert>}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={replyFormCtx.replyAnonymous}
                        onChange={e => replyFormCtx.setReplyAnonymous(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="caption" color="text.secondary">
                        Névtelenül (az adminok látják az azonosítód)
                      </Typography>
                    }
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" onClick={replyFormCtx.onCancelReply}>Mégse</Button>
                    <Button
                      variant="contained"
                      onClick={replyFormCtx.onSubmitReply}
                      disabled={replyFormCtx.submitting || !stripHtml(replyFormCtx.replyContent)}
                    >
                      {replyFormCtx.submitting ? 'Küldés...' : 'Küldés'}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Box>
          )}

          {/* Nested replies */}
          {reply.children?.map(child => (
            <ReplyCard
              key={child.id}
              reply={child}
              onReact={onReact}
              onReply={onReply}
              isAdmin={isAdmin}
              onHide={onHide}
              onExpert={onExpert}
              onEdit={onEdit}
              currentUserId={currentUserId}
              isAuthenticated={isAuthenticated}
              depth={depth + 1}
            />
          ))}
        </Box>
      )}

      <ReportDialog open={reportOpen} onClose={() => setReportOpen(false)} title="Tartalom bejelentése" onSubmit={handleReport} />
    </Box>
  );
};

const MoveEventBanner = ({ event }) => {
  const theme = useTheme();
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 2,
      py: 1,
      mb: 2,
      bgcolor: alpha(theme.palette.info.main, 0.06),
      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
      borderRadius: 1,
    }}>
      <SwapHoriz sx={{ fontSize: 16, color: 'info.main', flexShrink: 0 }} />
      <Typography variant="caption" color="text.secondary">
        A téma át lett helyezve: <strong>{event.fromCategoryName}</strong> → <strong>{event.toCategoryName}</strong>
        {' · '}{formatDate(event.movedAt)}
      </Typography>
    </Box>
  );
};

const ForumThread = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const [thread, setThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Válasz-piszkozat témánként: oldalfrissítés / véletlen elnavigálás után sem vész el
  const draftKey = `wedigo:forum:replyDraft:${id}`;
  const [replyContent, setReplyContent] = useState(() => {
    try { return localStorage.getItem(`wedigo:forum:replyDraft:${id}`) || ''; } catch { return ''; }
  });
  const [replyAnonymous, setReplyAnonymous] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [replyError, setReplyError] = useState(null);
  const quillEditorRef = useRef(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [editThreadMode, setEditThreadMode] = useState(false);
  const [editThreadContent, setEditThreadContent] = useState('');
  const [editThreadSaving, setEditThreadSaving] = useState(false);
  const [editThreadError, setEditThreadError] = useState(null);
  const replyRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      try {
        if (stripHtml(replyContent)) localStorage.setItem(draftKey, replyContent);
        else localStorage.removeItem(draftKey);
      } catch { /* nem kritikus */ }
    }, 600);
    return () => clearTimeout(t);
  }, [replyContent, draftKey]);

  const isAdmin = user?.userType === 'ADMIN';

  const loadData = useCallback(async () => {
    try {
      const [threadRes, repliesRes] = await Promise.all([
        forumService.getThread(id),
        forumService.getReplies(id)
      ]);
      if (threadRes.success) setThread(threadRes.data.thread);
      else setError('Téma nem található.');
      if (repliesRes.success) setReplies(buildTree(repliesRes.data.replies));
    } catch {
      setError('Nem sikerült betölteni a témát.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Deep link: URL hash alapján scrollozás a replyhoz
  useEffect(() => {
    if (!loading && location.hash) {
      const targetId = location.hash.replace('#', '');
      const el = document.getElementById(targetId);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.style.transition = 'background-color 0.3s';
          el.style.backgroundColor = alpha(theme.palette.warning.light, 0.3);
          setTimeout(() => { el.style.backgroundColor = ''; }, 2000);
        }, 300);
      }
    }
  }, [loading, location.hash, theme.palette.warning.light]);

  const handleReact = async (type, threadId, replyId) => {
    if (!isAuthenticated) { navigate('/login'); return; }
    await forumService.toggleReaction({ type, threadId, replyId });
    loadData();
  };

  const handleSubmitReply = async () => {
    if (stripHtml(replyContent).length < 2) {
      setReplyError('A válasz legalább 2 karakter legyen.');
      return;
    }
    if (!isAuthenticated) { navigate('/login'); return; }
    setSubmitting(true);
    setReplyError(null);
    try {
      const res = await forumService.createReply(id, {
        content: replyContent,
        parentId: replyingTo?.id,
        isAnonymous: replyAnonymous
      });
      if (res && res.success === false) throw new Error(res.message);
      setReplyContent('');
      quillEditorRef.current?.clear();
      try { localStorage.removeItem(draftKey); } catch { /* nem kritikus */ }
      setReplyAnonymous(false);
      setReplyingTo(null);
      loadData();
    } catch (err) {
      // A beírt szöveg megmarad, hogy újra lehessen próbálni
      setReplyError(err?.message || 'A válasz küldése nem sikerült. Próbáld újra.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplyTo = (reply) => {
    setReplyingTo(reply);
  };

  useEffect(() => {
    if (replyingTo) {
      setTimeout(() => {
        document.getElementById(`reply-${replyingTo.id}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }, [replyingTo]);

  const handleHideReply = async (replyId) => {
    await forumService.hideReply(replyId);
    loadData();
  };

  const handleExpertReply = async (replyId) => {
    await forumService.markExpertAnswer(replyId);
    loadData();
  };

  const handleEditReply = async (replyId, content) => {
    const res = await forumService.updateReply(replyId, { content });
    if (res && res.success === false) throw new Error(res.message);
    loadData();
  };

  const handlePinThread = async () => {
    await forumService.pinThread(id);
    loadData();
  };

  const handleLockThread = async () => {
    await forumService.lockThread(id);
    loadData();
  };

  const handleHideThread = async () => {
    const res = await forumService.hideThread(id);
    if (res?.success) loadData();
  };

  const handleEditThreadSave = async () => {
    if (stripHtml(editThreadContent).length < 10) {
      setEditThreadError('A tartalom legalább 10 karakter legyen.');
      return;
    }
    setEditThreadSaving(true);
    setEditThreadError(null);
    try {
      const res = await forumService.updateThread(id, { content: editThreadContent });
      if (res && res.success === false) throw new Error(res.message);
      setEditThreadMode(false);
      loadData();
    } catch (err) {
      setEditThreadError(err?.message || 'A mentés nem sikerült.');
    } finally {
      setEditThreadSaving(false);
    }
  };

  const handleReport = async (reason) => {
    const res = await forumService.report({ threadId: parseInt(id), reason });
    if (res && res.success === false) throw new Error(res.message);
  };

  const scrollToReplyForm = () => {
    replyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleThreadContentClick = (e) => {
    if (e.target.tagName === 'A') {
      if (!isAuthenticated) {
        e.preventDefault();
        navigate('/login');
      }
    }
  };

  const isThreadOwner = user && thread?.isOwnThread;

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
  );
  if (error) return (
    <Container maxWidth="md" sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>
  );

  return (
    <>
      <Helmet>
        <title>{thread?.title ?? 'Téma'} – Wedigo Fórum</title>
      </Helmet>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Breadcrumb */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button component={Link} to="/forum" size="small" startIcon={<ArrowBack />} sx={{ textTransform: 'none' }}>
            Fórum
          </Button>
          <Typography color="text.disabled">/</Typography>
          {thread?.category && (
            <Button component={Link} to={`/forum/${thread.category.slug}`} size="small" sx={{ textTransform: 'none' }}>
              {thread.category.icon} {thread.category.name}
            </Button>
          )}
        </Box>

        {/* Thread */}
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
            {thread?.isPinned && <Chip icon={<PushPin sx={{ fontSize: 14 }} />} label="Kitűzött" size="small" color="warning" />}
            {thread?.isLocked && <Chip icon={<Lock sx={{ fontSize: 14 }} />} label="Lezárt" size="small" />}
            {thread?.isHidden && <Chip label="Rejtett" size="small" color="error" />}
          </Box>

          <Typography variant="h5" fontFamily="Playfair Display, serif" fontWeight={700} gutterBottom>
            {thread?.title}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: 2, minWidth: 0 }}>
            <Avatar
              src={!thread?.isAnonymous && thread?.author?.avatarUrl ? thread.author.avatarUrl : undefined}
              sx={{ width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 }, bgcolor: theme.palette.primary.main, flexShrink: 0 }}
            >
              {thread?.isAnonymous ? '?' : (thread?.author?.firstName?.[0] ?? '?')}
            </Avatar>
            <Box>
              <AuthorLabel author={thread?.author} isAnonymous={thread?.isAnonymous} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(thread?.createdAt)} · {thread?.viewCount} megtekintés · {thread?.replyCount} válasz
                </Typography>
                {thread?.isEdited && (
                  <Typography variant="caption" color="text.secondary" fontStyle="italic">(szerkesztve)</Typography>
                )}
              </Box>
            </Box>
          </Box>

          {thread?.tags?.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
              {thread.tags.map(tag => (
                <Chip key={tag} label={tag} size="small"
                  sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.3), height: 22 }} />
              ))}
            </Box>
          )}

          {editThreadMode ? (
            <Box sx={{ mb: 2 }}>
              <Box sx={{
                mb: 1,
                border: `1px solid ${editThreadError ? theme.palette.error.main : theme.palette.divider}`,
                borderRadius: 1,
                overflow: 'hidden',
                '& .ql-editor': { minHeight: 140, maxHeight: 360, overflowY: 'auto', wordBreak: 'break-word', overflowX: 'hidden' },
                '& .ql-toolbar': { display: 'flex', flexWrap: 'wrap' }
              }}>
                <QuillEditor
                  value={editThreadContent}
                  onChange={setEditThreadContent}
                  modules={QUILL_MODULES}
                />
              </Box>
              {editThreadError && <Alert severity="error" sx={{ mb: 1 }}>{editThreadError}</Alert>}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" size="small" onClick={handleEditThreadSave} disabled={editThreadSaving}>
                  {editThreadSaving ? 'Mentés...' : 'Mentés'}
                </Button>
                <Button size="small" disabled={editThreadSaving} onClick={() => { setEditThreadMode(false); setEditThreadError(null); setEditThreadContent(thread.content); }}>Mégse</Button>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{ mb: 2, '& p': { margin: '0 0 8px 0' }, '& img': { maxWidth: '100%', borderRadius: 1 }, wordBreak: 'break-word', overflowWrap: 'break-word' }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(thread?.content ?? '') }}
              onClick={handleThreadContentClick}
            />
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <ReactionBar
              reactions={thread?.reactionCounts}
              userReactions={thread?.userReactions}
              onReact={(type) => handleReact(type, parseInt(id), null)}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {isThreadOwner && !editThreadMode && (
                <Button size="small" startIcon={<EditIcon />} onClick={() => { setEditThreadContent(thread.content); setEditThreadMode(true); }}
                  sx={{ textTransform: 'none', minHeight: 36 }}>
                  Szerkesztés
                </Button>
              )}
              <Button size="small" startIcon={<Flag />} onClick={() => setReportOpen(true)}
                sx={{ color: 'text.secondary', textTransform: 'none', minHeight: 36 }}>
                Bejelentés
              </Button>
              {isAdmin && (
                <>
                  <Button size="small" onClick={handlePinThread} sx={{ textTransform: 'none' }}>
                    {thread?.isPinned ? 'Kitűzés levétele' : 'Kitűzés'}
                  </Button>
                  <Button size="small" onClick={handleLockThread} sx={{ textTransform: 'none' }}>
                    {thread?.isLocked ? 'Lezárás feloldása' : 'Lezárás'}
                  </Button>
                  <Button size="small" color={thread?.isHidden ? 'success' : 'error'} onClick={handleHideThread} sx={{ textTransform: 'none' }}>
                    {thread?.isHidden ? 'Visszahozás' : 'Elrejtés'}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Move events */}
        {thread?.moveEvents?.map(event => (
          <MoveEventBanner key={event.id} event={event} />
        ))}

        {/* Válaszok */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" component="h2" fontWeight={600}>
            {replies.length} válasz
          </Typography>
          {!thread?.isLocked && replies.length > 3 && (
            <Button size="small" variant="outlined" startIcon={<ReplyIcon />} onClick={scrollToReplyForm} sx={{ textTransform: 'none', minHeight: 36 }}>
              Hozzászólok
            </Button>
          )}
        </Box>

        {replies.length === 0 && !thread?.isLocked && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Még nincs válasz – légy te az első, aki hozzászól!
          </Typography>
        )}

        <ReplyFormContext.Provider value={{
          replyingTo,
          replyContent,
          setReplyContent,
          replyAnonymous,
          setReplyAnonymous,
          submitting,
          replyError,
          onSubmitReply: handleSubmitReply,
          onCancelReply: () => setReplyingTo(null),
          quillEditorRef,
        }}>
          {replies.map(reply => (
            <ReplyCard
              key={reply.id}
              reply={reply}
              onReact={handleReact}
              onReply={handleReplyTo}
              isAdmin={isAdmin}
              onHide={handleHideReply}
              onExpert={handleExpertReply}
              onEdit={handleEditReply}
              currentUserId={user?.id}
              isAuthenticated={isAuthenticated}
            />
          ))}

          <Divider sx={{ my: 3 }} />

          {/* Válasz írása */}
          <Box ref={replyRef}>
            {thread?.isLocked ? (
              <Alert severity="info">Ez a téma le van zárva, nem lehet új választ írni.</Alert>
            ) : !isAuthenticated ? (
              <Alert severity="info" action={
                <Button component={Link} to="/login" size="small" variant="outlined">Bejelentkezés</Button>
              }>
                A válaszhoz be kell jelentkezned.
              </Alert>
            ) : !replyingTo ? (
              <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Hozzászólás</Typography>
                <Box sx={{ mb: 1, '& .ql-editor': { minHeight: 100, maxHeight: 300, overflowY: 'auto', wordBreak: 'break-word', overflowX: 'hidden' }, '& .ql-toolbar': { display: 'flex', flexWrap: 'wrap' } }}>
                  <QuillEditor
                    ref={quillEditorRef}
                    value={replyContent}
                    onChange={setReplyContent}
                    modules={QUILL_MODULES}
                    placeholder="Írd ide a hozzászólásodat..."
                  />
                </Box>
                {replyError && <Alert severity="error" sx={{ mb: 1 }}>{replyError}</Alert>}
                {replyContent && stripHtml(replyContent).length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    A hozzászólásod piszkozatként automatikusan mentődik.
                  </Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={replyAnonymous}
                        onChange={e => setReplyAnonymous(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="caption" color="text.secondary">
                        Névtelenül (az adminok látják az azonosítód)
                      </Typography>
                    }
                  />
                  <Button
                    variant="contained"
                    onClick={handleSubmitReply}
                    disabled={submitting || !stripHtml(replyContent)}
                  >
                    {submitting ? 'Küldés...' : 'Küldés'}
                  </Button>
                </Box>
              </Paper>
            ) : null}
          </Box>
        </ReplyFormContext.Provider>

        <ReportDialog open={reportOpen} onClose={() => setReportOpen(false)} title="Téma bejelentése" onSubmit={handleReport} />
      </Container>
    </>
  );
};

export default ForumThread;
